// API-level request/response encryption: RSA-OAEP (SHA-256) + AES-256-GCM.
//
// Scheme (hybrid encryption - RSA alone can't handle arbitrary payload sizes):
//  - A fresh random AES-256 key is generated for every request.
//  - The request body (if any) is encrypted with that AES key (AES-256-GCM).
//  - The AES key itself is encrypted with the backend's RSA public key
//    (RSA-OAEP / SHA-256) and sent in the `X-Encrypted-Key` header.
//  - The backend decrypts the AES key with its private key, decrypts the
//    body, and encrypts its reply with the SAME AES key - so the frontend
//    can decrypt the response without any further key exchange.
//
// Wire format for an encrypted body (request or response):
//   { "iv": "<base64, 12 random bytes>",
//     "payload": "<base64 ciphertext, with the 16-byte GCM auth tag appended>" }
//
// See BACKEND_ENCRYPTION.md at the repo root for the matching Node.js/Express
// implementation and the private key.

// Public key only - safe to commit. The matching private key lives on the
// backend only; see BACKEND_ENCRYPTION.md (not committed to this repo).
const BACKEND_PUBLIC_KEY_PEM = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAs8hQ37Sh37OgWbLTUk7C
gYPsVJvGb6WSDozFdXokniqA++ayaAf34dtJsViwUqLWeMj0Ij513u1AX1YKtlt8
UDzpEOMZwgfaRh54ftJPYTxb5/A04sec45uS396X4u4UBKn7xOphgB85MxFwYC74
82PjnYQsACduj/KVXbngfBqKKAGpQYP1CsqIKa+59/yds59PAIiw74vxJeYM/OIo
m7qsBPhjdtgbxKwLzKX4Go/GwO1WBLnzAfMOcs+2LvUrAWKbDXSMi8H3+6iEPlJ0
Ui/8oZxdNl6i5AZvrfXgJSUHqcL5rfrNYCn4d4e7F7IHmKbh2OERn3AL1r70NZ3R
dQIDAQAB
-----END PUBLIC KEY-----`;

let cachedPublicKeyPromise = null;

function base64ToBytes(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function bytesToBase64(bytes) {
  let binary = "";
  const chunkSize = 0x8000; // avoid call-stack limits on String.fromCharCode for large buffers
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

function pemToArrayBuffer(pem) {
  const b64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/, "")
    .replace(/-----END PUBLIC KEY-----/, "")
    .replace(/\s+/g, "");
  return base64ToBytes(b64).buffer;
}

function getPublicKey() {
  if (!cachedPublicKeyPromise) {
    cachedPublicKeyPromise = window.crypto.subtle.importKey(
      "spki",
      pemToArrayBuffer(BACKEND_PUBLIC_KEY_PEM),
      { name: "RSA-OAEP", hash: "SHA-256" },
      false,
      ["encrypt"]
    );
  }
  return cachedPublicKeyPromise;
}

/**
 * Encrypts `data` (any JSON-serializable value, or undefined/null for a
 * bodyless request) for one request.
 *
 * Returns:
 *  - envelope: the { iv, payload } object to send as the request body, or
 *    null if there was no data to encrypt (still returns a key so the
 *    response can be encrypted).
 *  - encryptedKeyB64: the RSA-encrypted AES key, for the X-Encrypted-Key header.
 *  - rawAesKey: the raw AES key bytes, kept in memory only, to decrypt the
 *    matching response - never sent over the wire itself.
 */
export async function encryptRequest(data) {
  const aesKey = await window.crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
  const rawAesKey = await window.crypto.subtle.exportKey("raw", aesKey);

  const publicKey = await getPublicKey();
  const encryptedKeyBuffer = await window.crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    publicKey,
    rawAesKey
  );

  let envelope = null;
  if (data !== undefined && data !== null) {
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const plaintext = new TextEncoder().encode(JSON.stringify(data));
    const ciphertext = await window.crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      aesKey,
      plaintext
    );
    envelope = {
      iv: bytesToBase64(iv),
      payload: bytesToBase64(new Uint8Array(ciphertext)),
    };
  }

  return {
    envelope,
    encryptedKeyB64: bytesToBase64(new Uint8Array(encryptedKeyBuffer)),
    rawAesKey,
  };
}

/** Decrypts a response envelope `{ iv, payload }` using the AES key generated for the matching request. */
export async function decryptResponse(envelope, rawAesKey) {
  const aesKey = await window.crypto.subtle.importKey(
    "raw",
    rawAesKey,
    { name: "AES-GCM" },
    false,
    ["decrypt"]
  );
  const iv = base64ToBytes(envelope.iv);
  const ciphertext = base64ToBytes(envelope.payload);
  const plaintextBuffer = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    aesKey,
    ciphertext
  );
  const text = new TextDecoder().decode(plaintextBuffer);
  return JSON.parse(text);
}

/** True if a response body looks like one of our encrypted envelopes. */
export function isEncryptedEnvelope(body) {
  return (
    !!body &&
    typeof body === "object" &&
    typeof body.iv === "string" &&
    typeof body.payload === "string"
  );
}
